/**
 * Country dial codes for the Book Shipment phone field.
 *
 * These are bundled rather than fetched from master data on purpose: the
 * `countries` table has a `phone_code` column, but the Sequelize model does not
 * map it and no seeder populates it, so the public
 * `/enquiries/public/:orgSlug/countries` endpoint can only ever return
 * `{ id, name }`. If that column is ever populated and exposed, this list can be
 * dropped in favour of the API.
 *
 * `name` must match the country names coming back from master data for
 * `dialCodeForCountry` to auto-select on country change; an unmatched name just
 * leaves the current selection alone.
 */

export type DialCode = {
  /** ISO-3166 alpha-2, used only as a stable React key. */
  iso: string;
  name: string;
  code: string;
  /**
   * Length range of the national significant number — the digits after the dial
   * code, with any trunk `0` already stripped.
   *
   * This is a DIGIT-COUNT check, not real phone-number validation: it rejects
   * obviously wrong lengths but will happily accept a correctly-sized number
   * with an invalid area/carrier prefix. Ranges are widened to cover both
   * landline and mobile where a country differs. If you need true validation
   * (prefix tables, formatting, type detection), swap this for
   * `libphonenumber-js` — it is the same data Google ships in Android.
   */
  min: number;
  max: number;
};

/** Selected when the form first loads — Sahari Cargo is UAE-based. */
export const DEFAULT_DIAL_CODE = '+971';

/* The six Sahari destinations first, then the rest by name. Sorted at render
   time, so order here is only for readability. */
export const DIAL_CODES: DialCode[] = [
  { iso: 'AE', name: 'United Arab Emirates', code: '+971', min: 8, max: 9 },
  { iso: 'BN', name: 'Brunei', code: '+673', min: 7, max: 7 },
  { iso: 'BD', name: 'Bangladesh', code: '+880', min: 8, max: 10 },
  { iso: 'IN', name: 'India', code: '+91', min: 10, max: 10 },
  { iso: 'PH', name: 'Philippines', code: '+63', min: 9, max: 10 },
  { iso: 'LK', name: 'Sri Lanka', code: '+94', min: 9, max: 9 },
  { iso: 'MY', name: 'Malaysia', code: '+60', min: 8, max: 10 },

  { iso: 'AU', name: 'Australia', code: '+61', min: 9, max: 9 },
  { iso: 'BH', name: 'Bahrain', code: '+973', min: 8, max: 8 },
  { iso: 'CA', name: 'Canada', code: '+1', min: 10, max: 10 },
  { iso: 'CN', name: 'China', code: '+86', min: 10, max: 11 },
  { iso: 'EG', name: 'Egypt', code: '+20', min: 9, max: 10 },
  { iso: 'FR', name: 'France', code: '+33', min: 9, max: 9 },
  { iso: 'DE', name: 'Germany', code: '+49', min: 6, max: 11 },
  { iso: 'HK', name: 'Hong Kong', code: '+852', min: 8, max: 8 },
  { iso: 'ID', name: 'Indonesia', code: '+62', min: 9, max: 12 },
  { iso: 'IR', name: 'Iran', code: '+98', min: 10, max: 10 },
  { iso: 'HU', name: 'Hungary', code: '+36', min: 8, max: 9 },
  { iso: 'IQ', name: 'Iraq', code: '+964', min: 8, max: 10 },
  { iso: 'IT', name: 'Italy', code: '+39', min: 9, max: 11 },
  { iso: 'JP', name: 'Japan', code: '+81', min: 9, max: 10 },
  { iso: 'JO', name: 'Jordan', code: '+962', min: 8, max: 9 },
  { iso: 'KE', name: 'Kenya', code: '+254', min: 9, max: 9 },
  { iso: 'KW', name: 'Kuwait', code: '+965', min: 8, max: 8 },
  { iso: 'LB', name: 'Lebanon', code: '+961', min: 7, max: 8 },
  { iso: 'MV', name: 'Maldives', code: '+960', min: 7, max: 7 },
  { iso: 'NP', name: 'Nepal', code: '+977', min: 8, max: 10 },
  { iso: 'NZ', name: 'New Zealand', code: '+64', min: 8, max: 10 },
  { iso: 'NG', name: 'Nigeria', code: '+234', min: 7, max: 10 },
  { iso: 'OM', name: 'Oman', code: '+968', min: 8, max: 8 },
  { iso: 'PK', name: 'Pakistan', code: '+92', min: 10, max: 10 },
  { iso: 'QA', name: 'Qatar', code: '+974', min: 8, max: 8 },
  { iso: 'RU', name: 'Russia', code: '+7', min: 10, max: 10 },
  { iso: 'SA', name: 'Saudi Arabia', code: '+966', min: 9, max: 9 },
  { iso: 'SG', name: 'Singapore', code: '+65', min: 8, max: 8 },
  { iso: 'ZA', name: 'South Africa', code: '+27', min: 9, max: 9 },
  { iso: 'KR', name: 'South Korea', code: '+82', min: 9, max: 10 },
  { iso: 'ES', name: 'Spain', code: '+34', min: 9, max: 9 },
  { iso: 'TH', name: 'Thailand', code: '+66', min: 9, max: 9 },
  { iso: 'TR', name: 'Turkey', code: '+90', min: 10, max: 10 },
  { iso: 'GB', name: 'United Kingdom', code: '+44', min: 9, max: 10 },
  { iso: 'US', name: 'United States', code: '+1', min: 10, max: 10 },
  { iso: 'VN', name: 'Vietnam', code: '+84', min: 9, max: 10 },
  { iso: 'YE', name: 'Yemen', code: '+967', min: 7, max: 9 },
];

/** Options in the order the <select> should render them. */
export const DIAL_CODE_OPTIONS = [...DIAL_CODES].sort((a, b) =>
  a.name.localeCompare(b.name),
);

/** `undefined` when the country isn't in the list — callers keep their current code. */
export const dialCodeForCountry = (country: string): string | undefined =>
  DIAL_CODES.find(
    (d) => d.name.toLowerCase() === country.trim().toLowerCase(),
  )?.code;

/**
 * What the user typed → the national significant number.
 *
 * Drops everything that isn't a digit (spaces, dashes, brackets) and strips a
 * leading trunk `0` — these numbers are written `050…` domestically, but `0` is
 * invalid once a dial code is prefixed.
 */
export const nationalDigits = (raw: string): string =>
  raw.replace(/\D/g, '').replace(/^0+/, '');

/* `+1` is both the US and Canada; they share a length, so the first match is
   safe here even though it is ambiguous for display. */
const entryForCode = (code: string): DialCode | undefined =>
  DIAL_CODES.find((d) => d.code === code);

/**
 * Validates the number against the dial code's expected digit count.
 * @returns a message to show the user, or `null` when the number looks valid.
 */
export const phoneProblem = (code: string, raw: string): string | null => {
  const digits = nationalDigits(raw);
  if (!digits) return 'Enter a phone number.';

  const entry = entryForCode(code);
  if (!entry) {
    // Unknown code — fall back to the E.164 bounds (max 15 digits incl. code).
    return digits.length >= 4 && digits.length <= 15
      ? null
      : 'Enter a valid phone number.';
  }

  if (digits.length < entry.min || digits.length > entry.max) {
    const expected =
      entry.min === entry.max
        ? `${entry.min} digits`
        : `${entry.min}–${entry.max} digits`;
    return `${entry.name} numbers are ${expected} after ${entry.code} — you entered ${digits.length}.`;
  }
  return null;
};
